package fr.frux.shared.data.repository

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.network.api.SpeciesApi
import fr.frux.core.network.api.SpeciesFetch
import fr.frux.core.network.error.mapFailureToAppError
import fr.frux.shared.data.local.NoOpSpeciesLocalDataSource
import fr.frux.shared.data.local.SpeciesLocalDataSource
import fr.frux.shared.data.mapper.toDomain
import fr.frux.shared.di.IoDispatcher
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.repository.SpeciesRepository
import fr.frux.shared.dto.SpeciesDto
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withContext

/**
 * Cache memoire du referentiel, adosse au cache persistant (#177). Le mutex fusionne les demandes
 * concurrentes du demarrage; un echec n'est jamais cache afin qu'un retry puisse retenter le
 * reseau. Sequence de chargement : lecture du cache local, revalidation conditionnelle (l'ETag
 * rejoue en If-None-Match epargne le corps si rien n'a change), et en cas d'echec reseau le cache
 * local est servi tel quel — le referentiel est quasi immuable, mieux vaut un catalogue date qu'un
 * ecran vide en foret. Le repli hors-ligne n'entre pas au cache memoire : l'appel suivant retente.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class SpeciesRepositoryImpl(
    private val speciesApi: SpeciesApi,
    @IoDispatcher private val dispatcher: CoroutineDispatcher,
    private val localDataSource: SpeciesLocalDataSource = NoOpSpeciesLocalDataSource,
) : SpeciesRepository {
    private val mutex = Mutex()
    private var cached: List<Species>? = null

    override suspend fun getSpecies(): Result<List<Species>> =
        mutex.withLock {
            cached?.let { return Result.success(it) }
            load()
        }

    override suspend fun suggestSpecies(
        name: String,
        comment: String?,
    ): Result<Unit> =
        runCatchingCancellable { speciesApi.suggestSpecies(name, comment) }
            .onFailure { Logger.e(TAG, "❌ Echec de l'envoi de la suggestion d'espece", it) }
            .mapFailureToAppError()

    private suspend fun load(): Result<List<Species>> =
        withContext(dispatcher) {
            val local = readLocal()
            runCatchingCancellable {
                Logger.d(TAG, "🌐 Chargement du referentiel d'especes (etag local : ${local?.etag})")
                when (val fetch = speciesApi.getSpecies(local?.etag)) {
                    is SpeciesFetch.Fetched -> {
                        writeLocal(fetch.etag, fetch.species)
                        fetch.species.map { it.toDomain() }
                    }

                    SpeciesFetch.NotModified -> {
                        Logger.d(TAG, "📦 Referentiel inchange (304), cache local servi")
                        checkNotNull(local).species.map { it.toDomain() }
                    }
                }
            }.onSuccess {
                cached = it
                Logger.i(TAG, "📚 Referentiel charge : ${it.size} especes")
            }.onFailure { Logger.e(TAG, "❌ Echec du chargement du referentiel", it) }
                .recoverCatching { error ->
                    val fallback = local ?: throw error
                    Logger.i(TAG, "📦 Reseau indisponible, referentiel local servi (${fallback.species.size} especes)")
                    fallback.species.map { it.toDomain() }
                }.mapFailureToAppError()
        }

    private suspend fun readLocal() =
        runCatchingCancellable { localDataSource.read() }
            .onFailure { Logger.e(TAG, "⚠️ Cache local especes illisible, ignore", it) }
            .getOrNull()

    private suspend fun writeLocal(
        etag: String?,
        species: List<SpeciesDto>,
    ) {
        runCatchingCancellable { localDataSource.write(etag, species) }
            .onFailure { Logger.e(TAG, "⚠️ Ecriture du cache local especes echouee, ignoree", it) }
    }

    companion object {
        private const val TAG = "SpeciesRepository"
    }
}
